import { ComponentProps } from "react";
import SplideArrows from "@components/Slider/components/SplideArrows";
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from "@greglaisph/react-splide";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import { Grid } from "@splidejs/splide-extension-grid";
import { IS_PLAYWRIGHT } from "@util/constant";

import "@greglaisph/react-splide/css/core";

type HomeSkyboxProps = {
  /** Additionl styling for the component */
  className?: string;
  /** The list of the articles */
  cardProps: ComponentProps<typeof Card>[];
};

/**
 * Renders the Home - skybox section with a list of articles.
 * https://www.figma.com/design/rmzJoZwUpb3iLm6lMDXJ2W/BT-MASTER--WEB-?node-id=1911-708&t=g7P8QlYHW3Ymfii6-4
 */
const HomeSkybox = ({
  className,
  cardProps,
}: HomeSkyboxProps): React.ReactElement => {
  const options: Options = {
    pagination: false,
    type: "slide",
    autoplay: !IS_PLAYWRIGHT,
    interval: 10000,
    perMove: 2,
    gap: "1rem",
    focus: 0,
    omitEnd: true,
    mediaQuery: "min",
    perPage: 2,
    fixedWidth: "90%",
    arrows: false,
    grid: { rows: 2, cols: 1, gap: { row: "1rem", col: "1rem" } },
    breakpoints: {
      768: { grid: null, fixedWidth: undefined, arrows: true },
      992: { perPage: 3, perMove: 3 },
      1200: { perPage: 4, perMove: 4 },
    },
  };

  const variant: CardVariations = {
    image: {
      position: "left",
      width: "w-[80px]",
      aspectRatio: "aspect-square",
      filters: { w: ImagePresets.MEDIUM, dpr: 1, f: "webp" },
      orientation: "square",
    },
    content: {
      width: "w-[calc(100%-80px)]",
      extraClass: "pl-4",
    },
    title: {
      color: "text-gray-850 !line-clamp-3",
      size: "text-base",
      extraClass: "font-lct font-bold leading-[1.3125rem]",
    },
    kicker: {
      color: "text-gray-850",
      extraClass: "tracking-[1px]",
    },
  };

  return (
    <div data-testid="home-skybox-component" className={className}>
      <Splide
        options={options}
        extensions={{ Grid }}
        tag="div"
        hasTrack={false}
        className="relative md:pr-9"
      >
        <SplideTrack>
          {cardProps.map((card) => {
            return (
              <SplideSlide key={card.id}>
                <Card {...card} variations={variant} />
              </SplideSlide>
            );
          })}
        </SplideTrack>

        <SplideArrows
          className="absolute bottom-0 right-0 top-0 flex flex-col items-center justify-center gap-2"
          prevArrowClassName="min-h-9 max-h-9"
          nextArrowClassName="min-h-9 max-h-9"
        />
      </Splide>
    </div>
  );
};

export default HomeSkybox;
